import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, Lock, Users, ArrowLeft, Globe } from 'lucide-react'

export default function VPNManagementPage() {
  const navigate = useNavigate()
  const [vpns, setVpns] = useState([
    { id: 1, name: 'Corporate VPN', status: 'Active', protocol: 'OpenVPN', users: 156, bandwidth: '450 Mbps', encryption: 'AES-256' },
    { id: 2, name: 'Remote Access VPN', status: 'Active', protocol: 'WireGuard', users: 89, bandwidth: '250 Mbps', encryption: 'ChaCha20' },
    { id: 3, name: 'Branch Office VPN', status: 'Active', protocol: 'IPSec', users: 42, bandwidth: '150 Mbps', encryption: 'AES-128' },
    { id: 4, name: 'Partner VPN', status: 'Inactive', protocol: 'OpenVPN', users: 0, bandwidth: '0 Mbps', encryption: 'AES-256' },
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingVpn, setEditingVpn] = useState(null)
  const [formData, setFormData] = useState({ name: '', protocol: 'OpenVPN', status: 'Active', users: 0, bandwidth: '', encryption: 'AES-256' })

  const stats = useMemo(() => ({
    totalVpns: vpns.length,
    activeVpns: vpns.filter(v => v.status === 'Active').length,
    totalUsers: vpns.reduce((sum, v) => sum + v.users, 0),
    totalBandwidth: vpns.reduce((sum, v) => sum + parseInt(v.bandwidth), 0)
  }), [vpns])

  const handleCreate = () => {
    setEditingVpn(null)
    setFormData({ name: '', protocol: 'OpenVPN', status: 'Active', users: 0, bandwidth: '', encryption: 'AES-256' })
    setShowModal(true)
  }

  const handleEdit = (vpn) => {
    setEditingVpn(vpn)
    setFormData(vpn)
    setShowModal(true)
  }

  const handleSave = () => {
    if (!formData.name) return alert('VPN name required')
    if (editingVpn) {
      setVpns(vpns.map(v => v.id === editingVpn.id ? { ...v, ...formData } : v))
    } else {
      setVpns([...vpns, { id: Date.now(), ...formData }])
    }
    setShowModal(false)
  }

  const handleDelete = (id) => {
    if (confirm('Delete this VPN?')) {
      setVpns(vpns.filter(v => v.id !== id))
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/network')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Network Management
        </button>

        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">VPN Management</h1>
              <p className="text-slate-400">Secure VPN connections and remote access control</p>
            </div>
            <button onClick={handleCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold">
              <Plus size={20} /> Add VPN
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total VPNs</div>
            <div className="text-3xl font-bold text-blue-400">{stats.totalVpns}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Active VPNs</div>
            <div className="text-3xl font-bold text-green-400">{stats.activeVpns}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Connected Users</div>
            <div className="text-3xl font-bold text-purple-400">{stats.totalUsers}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Bandwidth</div>
            <div className="text-3xl font-bold text-cyan-400">{stats.totalBandwidth} Mbps</div>
          </div>
        </div>

        <div className="space-y-4">
          {vpns.map((vpn) => (
            <div key={vpn.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Lock className="text-blue-400" size={20} />
                    <h3 className="text-lg font-semibold text-white">{vpn.name}</h3>
                    <span className={`px-3 py-1 rounded text-xs font-semibold ${vpn.status === 'Active' ? 'bg-green-600/20 text-green-400' : 'bg-slate-600/20 text-slate-400'}`}>{vpn.status}</span>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mt-4">
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Protocol</p>
                      <p className="text-white font-bold mt-1">{vpn.protocol}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Connected Users</p>
                      <p className="text-white font-bold mt-1">{vpn.users}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Bandwidth</p>
                      <p className="text-white font-bold mt-1">{vpn.bandwidth}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Encryption</p>
                      <p className="text-white font-bold mt-1">{vpn.encryption}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(vpn)} className="p-2 hover:bg-blue-500/20 rounded">
                    <Edit2 size={18} className="text-blue-400" />
                  </button>
                  <button onClick={() => handleDelete(vpn.id)} className="p-2 hover:bg-red-500/20 rounded">
                    <Trash2 size={18} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <h2 className="text-2xl font-bold text-white mb-6">{editingVpn ? 'Edit VPN' : 'Add VPN'}</h2>
              <div className="space-y-4 mb-6">
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="VPN Name" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <div className="grid grid-cols-2 gap-4">
                  <select value={formData.protocol} onChange={(e) => setFormData({...formData, protocol: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                    <option>OpenVPN</option>
                    <option>WireGuard</option>
                    <option>IPSec</option>
                  </select>
                  <input type="number" value={formData.users} onChange={(e) => setFormData({...formData, users: parseInt(e.target.value)})} placeholder="Connected Users" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" value={formData.bandwidth} onChange={(e) => setFormData({...formData, bandwidth: e.target.value})} placeholder="Bandwidth (Mbps)" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" />
                  <select value={formData.encryption} onChange={(e) => setFormData({...formData, encryption: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                    <option>AES-256</option>
                    <option>AES-128</option>
                    <option>ChaCha20</option>
                  </select>
                </div>
                <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                  <option>Active</option>
                  <option>Inactive</option>
                  <option>Maintenance</option>
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-semibold">Cancel</button>
                <button onClick={handleSave} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
